import {useMemo} from 'react';

import {useRunSetsQuery} from '../../../state/runs/hooks';
import {type Grouping} from '../../../state/views/groupSelections/types';
import {runColor} from '../../../util/colors';
import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {type MediaBrowserPanelProps} from '../Component';

/**
 * Fetches and enriches runs with color, entity/project, and lifecycle fields.
 *
 * `runState` is the filtered run's backend status.
 */
export function useAllRunsWithColors({
  data,
  query,
  pageQuery,
  customRunColors,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'pageQuery' | 'customRunColors' | 'query'
>): RunWithHistoryAndMediaWandb[] {
  const {runSetRefs} = usePanelsSharedRefsContext();
  const runSetsInfo = useRunSetsQuery({runSetRefs});

  // Build mapping of run set ID to grouping config
  const idToGroupingMap = useMemo(() => {
    const map: {[key: string]: Grouping | undefined} = {};
    (pageQuery.runSets || []).forEach(runSet => {
      map[runSet.id] = runSet.grouping;
    });
    return map;
  }, [pageQuery.runSets]);

  // Build set of enabled run set IDs
  const runSetEnabledMap = useMemo(() => {
    const map = new Map();
    runSetsInfo.runSets?.forEach(s => {
      if (s.enabled === true) {
        map.set(s.id, true);
      }
    });
    return map;
  }, [runSetsInfo.runSets]);

  // Build mapping of query ID to query info
  const queryMap = useMemo(() => {
    const map = new Map();
    query.runsetQueries.forEach(q => {
      map.set(q.id, q);
    });
    return map;
  }, [query.runsetQueries]);

  // Filter to only enabled runs
  const filteredRuns = useMemo(() => {
    return data.histories.data.filter(r => {
      const run = data?.filteredRunsById?.[r.name];
      const runsetID = run?.runsetInfo?.id;
      return runsetID != null && runSetEnabledMap.has(runsetID);
    });
  }, [data.histories.data, data.filteredRunsById, runSetEnabledMap]);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  // Enrich runs with entity/project names, colors, and lifecycle fields
  const allRuns: RunWithHistoryAndMediaWandb[] = useMemo(() => {
    return filteredRuns.map(r => {
      const run = data.filteredRunsById[r.name];
      const runsetID = run.runsetInfo.id;
      const runsetQuery = queryMap.get(runsetID);

      const groupKeys = idToGroupingMap[run.runsetInfo.id];
      const color = groupKeys
        ? runColor({run, groupKeys, customRunColors, colorPaletteOption})
        : undefined;

      if (runsetQuery == null) {
        throw new Error(
          'Inconsistent data from the server, run does not have a matching runset'
        );
      }
      return {
        ...r,
        _wandb: run._wandb,
        entityName: runsetQuery.entityName,
        projectName: runsetQuery.projectName,
        color,
        runState: run.state,
      };
    });
  }, [
    filteredRuns,
    data.filteredRunsById,
    queryMap,
    idToGroupingMap,
    customRunColors,
    colorPaletteOption,
  ]);

  return allRuns;
}
